import React, { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './BrochureViewerPage.css';
import { COURSES_LIST_PATH, API_USER_ENROLLMENT } from '../../../Domain/Redux/Paths';
import { HttpClient } from '../../../Domain/Utils/ApiUtilies/Fetch/Fetch';
import { useSelector } from 'react-redux';
import {
  useGetComponentParametersSingle, useGetComponentParameters
} from '../../../Core/Hooks/HooksParameters/useGetComponentParameters';
import {
  TYPE_FETCH_PARAMETERS
} from '../../../Domain/Settings/Constants/Constants';
import { COMPONENTS } from '../../../Domain/Settings/Constants/ComponentsParameter/ComponentsParameter';
import { useCombinedObjects } from '../../../Domain/Utils/ApiUtilies/Parameters/Parameters';
import WhatsAppIcon from '../../Atoms/IconWhatsApp/IconWhatsApp';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import useSchedulerName from '../../../Core/Hooks/HookSchedulerName/useSchedulerName';
import { handleMessage } from '../../../Domain/Utils/TextUtilities/textUtilities';
import ModalGeneral from '../../Modals/ModalGeneral/ModalGeneral';
import { UserRegisterForm } from '../../Organisms/UserRegisterForm/UserRegisterForm';
import ModalNotice from '../../Modals/ModalNotice/ModalNotice';

const MSG_UPDATE = 'User enrollment updated';
const MSG_CREATE = 'User enrollment created';
const MSG_EQUAL_EMAIL = 'Your user can not be a reference';
const MSG_ENROLLMENT_EXIST = 'User enrollment already exists';
const MSG_CREATE_UPDATE = 'User enrollment created!';


const BrochureViewerPage = () => {
  const languageKey = useSelector((store) => store.languageTraslate.language);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState('');
  const [deadlineText, setDeadlineText] = useState('');
  const location = useLocation();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const pageRefs = useRef([]);
  const viewerRef = useRef(null);

  const [containerWidth, setContainerWidth] = useState(0);

  // --- MEJORA 2: Estado para controlar las páginas realmente visibles (virtualización) ---
  const [visiblePages, setVisiblePages] = useState(new Set([1]));
  const ModalNotice = lazy(() => import('../../Modals/ModalNotice/ModalNotice'));

  const [schedulerListCourse, setSchedulerListCourse] = useState([]);
  const { nameScheduler } = useSchedulerName(schedulerListCourse?.[0]?.id);
  const [nameForm, setNameForm] = useState('');
  const [showEnrrolmentForm, setShowEnrrolmentForm] = useState(false);
  const [loaderForm, setLoaderForm] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [modalSucces, setModalSucces] = useState(false);
  const [modalError, setModalError] = useState({ show: false, title: null, subtitle: null });
  const [showMobileIndicator, setShowMobileIndicator] = useState(false);
  const mobileIndicatorTimer = useRef(null);
  const [pdfError, setPdfError] = useState(null);
  const mainContentRef = useRef(null);

  const parametersMlt = useGetComponentParametersSingle(COMPONENTS.WHATSAPP_BUTTON, TYPE_FETCH_PARAMETERS.STANDARD);
  const parametersImage = useGetComponentParametersSingle(COMPONENTS.BROCHURE_PDF, TYPE_FETCH_PARAMETERS.DETAIL);
  const parametersFormMlt = useGetComponentParameters(COMPONENTS.WHATSAPP_BUTTON);
  const parametersPDF = useGetComponentParameters(COMPONENTS.BROCHURE_PDF);
  const parameters = useCombinedObjects(parametersMlt, parametersFormMlt, parametersPDF, parametersImage);

  // --- MEJORA 3: Memorizar el enlace de WhatsApp para evitar recalcularlo ---
  const whatsappLink = useMemo(() => {
    const number = parameters?.FIL_WHATSAPP?.value || '';
    const message = parameters?.MESSAGE_WHATAPP?.value || '';
    const cleanedPhoneNumber = number.replace(/\D/g, '');
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${cleanedPhoneNumber}${message ? `?text=${encodedMessage}` : ''}`;
  }, [parameters]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    console.log('%c✅ PDF Cargado con Éxito!', 'color: green; font-weight: bold;'); // <-- AVISO DE ÉXITO
    setNumPages(numPages);
    pageRefs.current = Array(numPages).fill(null).map((_, i) => pageRefs.current[i] || React.createRef());
    setVisiblePages(new Set([1])); // Reset a la primera página
    setTimeout(() => {
      if (viewerRef.current) {
        viewerRef.current.scrollTop = 0;
      }
    }, 50);
  }, []);

  const scrollToPage = useCallback((pageIndex) => {
    pageRefs.current[pageIndex]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const goToPrevPage = useCallback(() => {
    const newPage = Math.max(pageNumber - 1, 1);
    scrollToPage(newPage - 1);
  }, [pageNumber, scrollToPage]);

  const goToNextPage = useCallback(() => {
    const newPage = Math.min(pageNumber + 1, numPages);
    scrollToPage(newPage - 1);
  }, [pageNumber, numPages, scrollToPage]);

  const zoomIn = useCallback(() => setScale(s => Math.min(s + 0.2, 3.0)), []);
  const zoomOut = useCallback(() => setScale(s => Math.max(s - 0.2, 0.5)), []);

  useLayoutEffect(() => {
    const measureContainer = () => {
      // 1. Ahora medimos mainContentRef, que es más fiable
      if (mainContentRef.current) {
        const width = mainContentRef.current.clientWidth;
        // 2. Solo actualizamos si el ancho es diferente para evitar bucles
        if (width !== containerWidth) {
          console.log(`%cActualizando ancho del contenedor: ${width}px`, 'color: blue; font-weight: bold;');
          setContainerWidth(width);
        }
      }
    };

    measureContainer();

    window.addEventListener('resize', measureContainer);
    return () => window.removeEventListener('resize', measureContainer);
  }, [containerWidth]); // <-- 3. ¡EL CAMBIO MÁS IMPORTANTE! La dependencia que permite reintentar.


  useEffect(() => {
    if (!numPages) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const page = parseInt(entry.target.dataset.pageNumber, 10);
            console.log(`Observer: La página ${page} ahora es visible.`); // Log para depurar
            setPageNumber(page);
            // --- MEJORA 2 (cont.): Cargar la página actual, la anterior y la siguiente ---
            setVisiblePages(prev => new Set(prev).add(page - 1).add(page).add(page + 1));

            clearTimeout(mobileIndicatorTimer.current);
            setShowMobileIndicator(true);
            mobileIndicatorTimer.current = setTimeout(() => setShowMobileIndicator(false), 2000);
          }
        });
      },
      {
        root: mainContentRef.current, // ANTES: viewerRef.current
        threshold: 0.1,
        rootMargin: '200px 0px',
      }
    );

    pageRefs.current.forEach(ref => ref.current && observer.observe(ref.current));
    return () => {
      pageRefs.current.forEach(ref => ref.current && observer.unobserve(ref.current));
      clearTimeout(mobileIndicatorTimer.current);
    };
  }, [numPages]);

  // --- MEJORA 4: Memorizar la función de envío para optimizar el componente hijo ---
  const updateData = useCallback(async (data, method) => {
    setLoaderForm(true);
    try {
      const resEnrollment = await HttpClient[method](API_USER_ENROLLMENT, data);

      const message = resEnrollment?.message || '';
      if ([MSG_CREATE, MSG_UPDATE, MSG_CREATE_UPDATE].includes(message)) {
        setModalSucces(true);
      } else if (message === MSG_EQUAL_EMAIL) {
        setModalError({ show: true, title: parameters?.INF_MSG_55?.value, subtitle: parameters?.YOU_CANT_BE_REF?.value });
      } else if (message === MSG_ENROLLMENT_EXIST) {
        setModalError({ show: true, title: parameters?.INF_MSG_55?.value, subtitle: parameters?.ENROLLMENT_EXIST?.value ?? parameters?.YOU_CANT_BE_REF?.value });
      } else if (resEnrollment?.content?.includes('user phone already exists') || resEnrollment?.data?.includes('user phone already exists')) {
        setMessageError(handleMessage('user phone already exists', parameters));
        setLoaderForm(false);
        return;
      }
      setShowEnrrolmentForm(false);
    } catch (error) {
      console.error("Error updating data:", error);
    } finally {
      setLoaderForm(false);
    }
  }, [parameters]);

  useEffect(() => {
    if (!languageKey?.value) return;
    const loadInitialData = async () => {
      const courseId = new URLSearchParams(location.search).get('course');
      if (!courseId) {
        setError('No se ha especificado un curso.');
        return;
      }
      try {
        const courseResponse = await HttpClient.GET(COURSES_LIST_PATH, {
          course_id: courseId,
          language: languageKey.value,
          component: COMPONENTS.DETAIL_COURSE_INFORMATION
        });
        const course = courseResponse.results[0];
        if (!course?.brochure) {
          setError('No se ha proporcionado ningún archivo para visualizar.');
          return;
        }
        setPdfUrl(course.brochure);
        setSchedulerListCourse(course?.scheduler_fields);
        const startDate = course?.scheduler_fields[0]?.start_date;
        if (startDate) {
          const targetDate = new Date(startDate);
          const now = new Date();
          const daysRemaining = Math.ceil((targetDate - now) / (1000 * 60 * 60 * 24));
          const baseText = parameters?.BROCHURE_FOOTER?.value || 'Las solicitudes cierran en -X- dias';
          setDeadlineText(daysRemaining < 0 ? '' : baseText.replace('-X-', daysRemaining));
        } else {
          setDeadlineText('');
        }
      } catch (err) {
        console.error('>>>> BrochureViewerPage.loadInitialData: error=', err);
        setError('No se pudo cargar la información del curso.');
      }
    };
    loadInitialData();
  }, [languageKey, location.search, parameters]);

  console.log('Intentando renderizar con URL:', pdfUrl);
  return (
    <>
      {showEnrrolmentForm && (
        <ModalGeneral
          isShow={showEnrrolmentForm}
          handleShow={() => {
            setShowEnrrolmentForm(false);
          }}
          onHide={() => {
            setShowEnrrolmentForm(false);
          }}
          size={'lg'}
          title={`${parameters?.TXT_FORM?.value}  ${nameScheduler}`}
          subtitle={`${parameters?.INF_MSG_9?.value || 'Código:'} ${schedulerListCourse?.[0]?.id}`}
          bgHeader={'rgba(255, 233, 0, 0.05)'}
          children={
            <UserRegisterForm
              setCloseModal={setShowEnrrolmentForm}
              onSubmit={updateData}
              setNameForm={setNameForm}
              nameForm={nameForm}
              schedulerIdCourse={parseInt(schedulerListCourse?.[0]?.id)}
              isLms
              setLoaderForm={setLoaderForm}
              loaderForm={loaderForm}
              messageError={messageError}
              setMessageError={setMessageError}
            />
          }
        />
      )}

      {/* Lo mismo para los otros modales */}
      {modalSucces && (
        <ModalNotice
          success
          isShow={modalSucces}
          onHide={() => setModalSucces(false)}
          confirm={parameters?.ACCEPT_MODAL?.value}
          onClickConfirm={() => setModalSucces(false)}
          title={parameters?.SUCCESS_TRANSACTION?.value ?? 'Datos guardados con exito'}
          subtitle={parameters?.SUCCESS_TRANSACTION?.description_value ?? 'Éxito'}
        />
      )}

      {modalError.show && (
        <ModalNotice
          warning
          isShow={modalError.show}
          onHide={() => setModalError({ show: false, title: null, subtitle: null })}
          confirm={parameters?.ACCEPT_MODAL?.value}
          onClickConfirm={() => setModalError({ show: false, title: null, subtitle: null })}
          title={modalError.title}
          subtitle={modalError.subtitle}
        />
      )}



      <div className="viewer-page-wrapper">
        <div className="viewer-layout-container">
          <main ref={mainContentRef} className="pdf-main-content no-select">
            {error && <div className="error-message">{error}</div>}
            {pdfUrl && !error && (
              <>
                <div className="pdf-toolbar">
                  <div className="toolbar-group">
                    <button onClick={goToPrevPage} disabled={pageNumber <= 1} title="Página Anterior">&#x2191;</button>
                    <button onClick={goToNextPage} disabled={pageNumber >= numPages} title="Página Siguiente">&#x2193;</button>
                  </div>
                  <div className="toolbar-group">
                    <span>{parameters?.CRE_PAGE_LABEL?.value} {pageNumber} {"de"} {parameters?.CRE_PAGE_LABEL?.description_value} {numPages}</span>
                  </div>
                  <div className="toolbar-group">
                    <button onClick={zoomIn} disabled={scale >= 3.0} title="Aumentar Zoom">+</button>
                    <button onClick={zoomOut} disabled={scale <= 0.5} title="Disminuir Zoom">-</button>
                  </div>
                </div>
                <div className={`page-indicator-mobile ${showMobileIndicator ? 'visible' : ''}`}>
                  {parameters?.CRE_PAGE_LABEL?.value} {pageNumber} {"de"} {parameters?.CRE_PAGE_LABEL?.description_value} {numPages}
                </div>
                <div ref={viewerRef} className="pdf-viewer-container scrollable">
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error) => {
                      console.error('Error al cargar el PDF:', error);
                      setPdfError(`Falló la carga del PDF: ${error.message}`);
                    }}
                  >
                    {Array.from({ length: numPages }, (_, index) => {
                      const pageNum = index + 1;
                      return (
                        <div
                          key={`page_container_${pageNum}`}
                          ref={pageRefs.current[index]}
                          data-page-number={pageNum}
                          className="page-container"
                        >
                          {visiblePages.has(pageNum) && containerWidth > 0 ? ( // <-- Condición extra
                            <Page
                              pageNumber={pageNum}
                              // --- PASO 4: CAMBIA EL ANCHO FIJO POR EL ANCHO DEL ESTADO ---
                              width={containerWidth} // <-- ¡EL CAMBIO CLAVE!
                              scale={scale} // Puedes incluso querer eliminar el zoom manual en móvil
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          ) : (
                            // Placeholder para evitar saltos de layout (CLS)
                            <div style={{ minHeight: '1120px', width: '100%' }}></div>
                          )}
                        </div>
                      );
                    })}
                  </Document>
                </div>
              </>
            )}
          </main>
          <aside className="viewer-sidebar">
            <div className="cta-card">
              <div className="cta-icon-wrapper">
                {/* --- MEJORA 5: Optimización de imagen --- */}
                <img
                  src={parameters?.BROCHURE_IMAGE?.box}
                  alt="Ícono del programa"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="cta-content">
                <h3 className="cta-title">{parameters?.BROCHURE_HEADER?.value}</h3>
                <p className="cta-description">{parameters?.BROCHURE_TEXT?.value}</p>
              </div>
              {!!nameScheduler && (
                <button type="button" className="cta-button" onClick={() => setShowEnrrolmentForm(true)}>
                  {parameters?.BROCHURE_BUTTON?.value}
                </button>
              )}
              <p className="cta-footer-text">{deadlineText}</p>
            </div>
            <div className='line-separate'/>
            <div className="cta-card">
              <div className="cta-icon-wrapper icon-only">
                <WhatsAppIcon />
              </div>
              <div className="cta-content">
                <h3 className="cta-title">{parameters?.BROCHURE_HEADER?.description_value}</h3>
                <p className="cta-description">{parameters?.BROCHURE_TEXT?.description_value}</p>
              </div>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="cta-button">
                {parameters?.BROCHURE_BUTTON?.description_value}
              </a>
              <p className="cta-footer-text">{parameters?.BROCHURE_FOOTER?.description_value}</p>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
};

export default BrochureViewerPage;